import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { isCdm } from "features/cdm/constants/reviewTypes";

// Fetches the diagnosis codes of each CDM review so callers can tell which
// condition a submitted review belongs to (the status endpoint does not expose
// diagnoses synchronously). Returns a map keyed by review id.
export const useCdmReviewDiseaseIds = (reviews) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");

  const cdmReviews = useMemo(
    () => (reviews ?? []).filter((review) => isCdm(review.reviewType)),
    [reviews]
  );

  const queries = useQueries({
    queries: cdmReviews.map((review) => ({
      queryKey: ["cdmReviewDiseaseIds", review.id, tenantVal],
      queryFn: async () => {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(
          `${tenantVal}/cdm/reviews/${review.id}`
        );
        const diagnoses =
          response.data?.clinicalData?.diagnosis?.diagnoses ?? [];

        return diagnoses.map((diagnosis) => diagnosis.disease);
      },
      enabled: !!review.id && !!tenantVal,
      staleTime: 5 * 60 * 1000,
    })),
  });

  return useMemo(() => {
    const diseaseIdsByReview = {};

    cdmReviews.forEach((review, index) => {
      const result = queries[index];
      if (result?.data != null) {
        diseaseIdsByReview[review.id] = result.data;
      }
    });

    return diseaseIdsByReview;
  }, [cdmReviews, queries]);
};
